import { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { 
    User, 
    Heart, 
    AlertTriangle, 
    MapPin, 
    Phone, 
    Shield, 
    FileText,
    ChevronDown,
    ChevronUp,
    CheckCircle2,
    AlertCircle,
    Save,
    Loader2
} from 'lucide-react';

// ============================================
// LOCAL STORAGE KEY FOR AUTO-SAVE
// ============================================
const STORAGE_KEY = 'craven_canines_safety_form_draft';

// ============================================
// MEMOIZED SUB-COMPONENTS (defined outside main component)
// ============================================

const InputField = memo(({ label, name, value, onChange, type = 'text', placeholder, required = true, error }) => (
    <div className="space-y-2">
        <label htmlFor={name} className="block text-sm font-medium text-foreground">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            id={name}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            autoComplete="off"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
                error ? 'border-red-500 bg-red-50' : 'border-border'
            }`}
        />
        {error && (
            <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
            </p>
        )}
    </div>
));
InputField.displayName = 'InputField';

const TextAreaField = memo(({ label, name, value, onChange, placeholder, required = true, rows = 3, error }) => (
    <div className="space-y-2">
        <label htmlFor={name} className="block text-sm font-medium text-foreground">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <textarea
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors resize-none ${
                error ? 'border-red-500 bg-red-50' : 'border-border'
            }`}
        />
        {error && (
            <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
            </p>
        )}
    </div>
));
TextAreaField.displayName = 'TextAreaField';

const RadioGroup = memo(({ label, name, value, onChange, options, error }) => (
    <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground">
            {label} <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-4">
            {options.map((option) => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={value === option.value}
                        onChange={onChange}
                        className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-foreground">{option.label}</span>
                </label>
            ))}
        </div>
        {error && (
            <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
            </p>
        )}
    </div>
));
RadioGroup.displayName = 'RadioGroup';

const CheckboxField = memo(({ label, name, checked, onChange, linkText, linkUrl, error }) => (
    <div className="space-y-1">
        <label className="flex items-start gap-3 cursor-pointer">
            <input
                type="checkbox"
                name={name}
                checked={checked}
                onChange={onChange}
                className="w-5 h-5 mt-0.5 text-primary border-border rounded focus:ring-primary"
            />
            <span className="text-foreground text-sm leading-relaxed">
                {label}
                {linkText && linkUrl && (
                    <>
                        {' '}
                        <Link 
                            to={linkUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {linkText}
                        </Link>
                    </>
                )}
            </span>
        </label>
        {error && (
            <p className="text-red-500 text-sm flex items-center gap-1 ml-8">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
            </p>
        )}
    </div>
));
CheckboxField.displayName = 'CheckboxField';

const SectionHeader = memo(({ icon: Icon, title, section, expanded, onToggle, required = true, isComplete = false }) => (
    <button
        type="button"
        onClick={() => onToggle(section)}
        className="w-full flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
    >
        <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isComplete ? 'bg-green-100' : 'bg-primary/10'
            }`}>
                {isComplete ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                    <Icon className="w-5 h-5 text-primary" />
                )}
            </div>
            <span className="font-semibold text-foreground">{title}</span>
            {required && !isComplete && <span className="text-red-500 text-sm">*</span>}
            {isComplete && <span className="text-green-600 text-xs font-medium ml-2">Complete</span>}
        </div>
        {expanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
    </button>
));
SectionHeader.displayName = 'SectionHeader';

// ============================================
// INITIAL STATE
// ============================================
const initialFormData = {
    handlerName: '',
    mobileNumber: '',
    emailAddress: '',
    dogName: '',
    dogAge: '',
    dogBreed: '',
    dogGender: '',
    healthConditions: '',
    biteSportTraining: '',
    biteSportDetails: '',
    aggressionHistory: '',
    aggressionDetails: '',
    generalBehaviour: '',
    knownTriggers: '',
    environmentalTolerance: '',
    physicalLimitations: '',
    emergencyContact: '',
    additionalInfo: '',
    fitToTrain: false,
    onLeadAtAllTimes: false,
    remainInVehicle: false,
    publicEnvironments: false,
    termsAccepted: false,
    riskAssessmentRead: false
};

// ============================================
// PROGRESS INDICATOR COMPONENT
// ============================================
const ProgressIndicator = memo(({ completedSections, totalSections }) => {
    const percentage = Math.round((completedSections / totalSections) * 100);
    
    return (
        <div className="bg-white border border-border rounded-lg p-4 mb-6 sticky top-4 z-10 shadow-sm" data-testid="progress-indicator">
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Form Progress</span>
                <span className="text-sm font-semibold text-primary">{percentage}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                    className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>{completedSections} of {totalSections} sections complete</span>
                {percentage === 100 && (
                    <span className="text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Ready to proceed
                    </span>
                )}
            </div>
        </div>
    );
});
ProgressIndicator.displayName = 'ProgressIndicator';

// ============================================
// AUTO-SAVE INDICATOR COMPONENT
// ============================================
const AutoSaveIndicator = memo(({ isSaving, lastSaved }) => {
    if (!lastSaved && !isSaving) return null;
    
    return (
        <div className="fixed bottom-4 right-4 bg-white border border-border rounded-lg px-3 py-2 shadow-md flex items-center gap-2 text-sm z-50" data-testid="auto-save-indicator">
            {isSaving ? (
                <>
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-muted-foreground">Saving...</span>
                </>
            ) : (
                <>
                    <Save className="w-4 h-4 text-green-600" />
                    <span className="text-muted-foreground">Draft saved</span>
                </>
            )}
        </div>
    );
});
AutoSaveIndicator.displayName = 'AutoSaveIndicator';

// ============================================
// MAIN COMPONENT
// ============================================
const SafetyForm = ({ onFormValid, onFormData }) => {
    // Load initial data from localStorage or use defaults
    const getInitialFormData = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Merge with initial to handle any new fields
                return { ...initialFormData, ...parsed };
            }
        } catch (e) {
        }
        return initialFormData;
    };

    const [formData, setFormData] = useState(getInitialFormData);
    const [errors, setErrors] = useState({});
    const [expandedSections, setExpandedSections] = useState({
        handlerDog: true,
        health: true,
        behaviour: true,
        environment: true,
        handlerSafety: true,
        declarations: true,
        legal: true,
        additional: true
    });
    
    // Auto-save state
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const autoSaveTimeoutRef = useRef(null);
    
    // Use refs to avoid triggering re-renders in parent
    const formDataRef = useRef(formData);
    const validationTimeoutRef = useRef(null);

    // Update ref when formData changes
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    // Auto-save to localStorage with debounce
    useEffect(() => {
        // Clear previous timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        // Debounce auto-save to avoid excessive writes
        autoSaveTimeoutRef.current = setTimeout(() => {
            try {
                setIsSaving(true);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
                setLastSaved(new Date());
                setTimeout(() => setIsSaving(false), 500);
            } catch (e) {
                setIsSaving(false);
            }
        }, 1000);

        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [formData]);

    // Clear saved draft when form is successfully submitted (called externally)
    const clearDraft = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
            setLastSaved(null);
        } catch (e) {
        }
    }, []);

    // Validation function - memoized
    const validateForm = useCallback((data) => {
        const newErrors = {};

        if (!data.handlerName.trim()) newErrors.handlerName = 'Handler name is required';
        if (!data.mobileNumber.trim()) newErrors.mobileNumber = 'Mobile number is required';
        if (!data.emailAddress.trim()) {
            newErrors.emailAddress = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.emailAddress)) {
            newErrors.emailAddress = 'Please enter a valid email address';
        }
        if (!data.dogName.trim()) newErrors.dogName = "Dog's name is required";
        if (!data.dogAge.trim()) newErrors.dogAge = "Dog's age is required";
        if (!data.dogBreed.trim()) newErrors.dogBreed = "Dog's breed/type is required";
        if (!data.healthConditions.trim()) newErrors.healthConditions = 'Please list any conditions or write "none"';
        if (!data.biteSportTraining) newErrors.biteSportTraining = 'Please select an option';
        if (data.biteSportTraining === 'yes' && !data.biteSportDetails.trim()) {
            newErrors.biteSportDetails = 'Please provide details';
        }
        if (!data.aggressionHistory) newErrors.aggressionHistory = 'Please select an option';
        if (data.aggressionHistory === 'yes' && !data.aggressionDetails.trim()) {
            newErrors.aggressionDetails = 'Please provide details';
        }
        if (!data.generalBehaviour.trim()) newErrors.generalBehaviour = 'Please describe your dog\'s behaviour';
        if (!data.knownTriggers.trim()) newErrors.knownTriggers = 'Please list any triggers or write "none"';
        if (!data.environmentalTolerance.trim()) newErrors.environmentalTolerance = 'Please describe how your dog copes';
        if (!data.physicalLimitations.trim()) newErrors.physicalLimitations = 'Please describe or write "none"';
        if (!data.emergencyContact.trim()) newErrors.emergencyContact = 'Emergency contact is required';
        if (!data.fitToTrain) newErrors.fitToTrain = 'Required';
        if (!data.onLeadAtAllTimes) newErrors.onLeadAtAllTimes = 'Required';
        if (!data.remainInVehicle) newErrors.remainInVehicle = 'Required';
        if (!data.publicEnvironments) newErrors.publicEnvironments = 'Required';
        if (!data.termsAccepted) newErrors.termsAccepted = 'You must accept';
        if (!data.riskAssessmentRead) newErrors.riskAssessmentRead = 'You must confirm';

        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    }, []);

    // Debounced validation and parent notification
    useEffect(() => {
        // Clear previous timeout
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }

        // Debounce validation to avoid excessive parent updates
        validationTimeoutRef.current = setTimeout(() => {
            const { isValid } = validateForm(formData);
            onFormValid(isValid);
            onFormData(formData);
        }, 300);

        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };
    }, [formData, validateForm, onFormValid, onFormData]);

    // Generic change handler - stable reference
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({ ...prev, [name]: newValue }));
        
        // Clear error for this field
        setErrors(prev => {
            if (prev[name]) {
                const { [name]: _, ...rest } = prev;
                return rest;
            }
            return prev;
        });
    }, []);

    // Section toggle - stable reference
    const toggleSection = useCallback((section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    }, []);

    // Radio options - memoized to prevent recreation
    const yesNoOptions = useMemo(() => [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
    ], []);

    const aggressionOptions = useMemo(() => [
        { value: 'no', label: 'No history' },
        { value: 'yes', label: 'Yes' }
    ], []);

    // Calculate section completion for progress indicator
    const sectionCompletion = useMemo(() => {
        const sections = {
            handlerDog: Boolean(
                formData.handlerName.trim() && 
                formData.mobileNumber.trim() &&
                formData.emailAddress.trim() &&
                formData.dogName.trim() && 
                formData.dogAge.trim() && 
                formData.dogBreed.trim() &&
                formData.dogGender
            ),
            health: Boolean(formData.healthConditions.trim()),
            behaviour: Boolean(
                formData.biteSportTraining && 
                (formData.biteSportTraining === 'no' || formData.biteSportDetails.trim()) &&
                formData.aggressionHistory && 
                (formData.aggressionHistory === 'no' || formData.aggressionDetails.trim()) &&
                formData.generalBehaviour.trim() && 
                formData.knownTriggers.trim()
            ),
            environment: Boolean(formData.environmentalTolerance.trim()),
            handlerSafety: Boolean(
                formData.physicalLimitations.trim() && 
                formData.emergencyContact.trim()
            ),
            declarations: Boolean(
                formData.fitToTrain && 
                formData.onLeadAtAllTimes && 
                formData.remainInVehicle && 
                formData.publicEnvironments
            ),
            legal: Boolean(formData.termsAccepted && formData.riskAssessmentRead)
        };
        
        const completedCount = Object.values(sections).filter(Boolean).length;
        return { sections, completedCount, total: 7 };
    }, [formData]);

    // Scroll to first error when errors change
    const scrollToFirstError = useCallback((errorFields) => {
        if (Object.keys(errorFields).length === 0) return;
        
        const firstErrorField = Object.keys(errorFields)[0];
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the element after scroll completes
            setTimeout(() => {
                element.focus();
            }, 500);
        }
    }, []);

    return (
        <div className="space-y-6" data-testid="safety-form">
            {/* Progress Indicator */}
            <ProgressIndicator 
                completedSections={sectionCompletion.completedCount} 
                totalSections={sectionCompletion.total} 
            />

            {/* Form Title */}
            <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground font-serif mb-2">
                    Mantrailing Booking – Safety Information
                </h2>
                <p className="text-muted-foreground">
                    Please complete all required fields before proceeding to payment
                </p>
                {lastSaved && (
                    <p className="text-xs text-muted-foreground mt-2">
                        Your progress is automatically saved
                    </p>
                )}
            </div>

            {/* Section 1: Handler & Dog Details */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={User} 
                        title="1. Handler & Dog Details" 
                        section="handlerDog" 
                        expanded={expandedSections.handlerDog}
                        onToggle={toggleSection}
                        isComplete={sectionCompletion.sections.handlerDog}
                    />
                    {expandedSections.handlerDog && (
                        <div className="p-6 space-y-4 border-t">
                            <div className="grid md:grid-cols-2 gap-4">
                                <InputField 
                                    label="Handler Name" 
                                    name="handlerName"
                                    value={formData.handlerName}
                                    onChange={handleChange}
                                    placeholder="Your full name"
                                    error={errors.handlerName}
                                />
                                <InputField 
                                    label="Mobile Number" 
                                    name="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleChange}
                                    placeholder="e.g., 07700 900123"
                                    error={errors.mobileNumber}
                                />
                            </div>
                            <div className="grid md:grid-cols-1 gap-4">
                                <InputField 
                                    label="Email Address (for booking confirmation)" 
                                    name="emailAddress"
                                    type="email"
                                    value={formData.emailAddress}
                                    onChange={handleChange}
                                    placeholder="your@email.com"
                                    error={errors.emailAddress}
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <InputField 
                                    label="Dog's Name" 
                                    name="dogName"
                                    value={formData.dogName}
                                    onChange={handleChange}
                                    placeholder="Your dog's name"
                                    error={errors.dogName}
                                />
                                <InputField 
                                    label="Dog's Age" 
                                    name="dogAge"
                                    value={formData.dogAge}
                                    onChange={handleChange}
                                    placeholder="e.g., 3 years"
                                    error={errors.dogAge}
                                />
                            </div>
                            <div className="grid md:grid-cols-1 gap-4">
                                <InputField 
                                    label="Dog's Breed/Type" 
                                    name="dogBreed"
                                    value={formData.dogBreed}
                                    onChange={handleChange}
                                    placeholder="e.g., Border Collie"
                                    error={errors.dogBreed}
                                />
                            </div>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Dog's Gender <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer" data-testid="dog-gender-male">
                                        <input
                                            type="radio"
                                            name="dogGender"
                                            value="Male"
                                            checked={formData.dogGender === 'Male'}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                        />
                                        <span className="text-sm text-foreground">Male</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer" data-testid="dog-gender-female">
                                        <input
                                            type="radio"
                                            name="dogGender"
                                            value="Female"
                                            checked={formData.dogGender === 'Female'}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                        />
                                        <span className="text-sm text-foreground">Female</span>
                                    </label>
                                </div>
                                {errors.dogGender && (
                                    <p className="text-red-500 text-xs mt-1">{errors.dogGender}</p>
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Section 2: Dog Health & Fitness */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={Heart} 
                        title="2. Dog Health & Fitness" 
                        section="health"
                        expanded={expandedSections.health}
                        onToggle={toggleSection}
                        isComplete={sectionCompletion.sections.health}
                    />
                    {expandedSections.health && (
                        <div className="p-6 border-t">
                            <TextAreaField 
                                label="Please list any medical conditions, injuries, medication, or physical limitations (or write 'none')"
                                name="healthConditions"
                                value={formData.healthConditions}
                                onChange={handleChange}
                                placeholder="Describe any health conditions or write 'none'..."
                                rows={4}
                                error={errors.healthConditions}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Section 3: Behaviour & Risk Screening */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={AlertTriangle} 
                        title="3. Behaviour & Risk Screening" 
                        section="behaviour"
                        expanded={expandedSections.behaviour}
                        onToggle={toggleSection}
                        isComplete={sectionCompletion.sections.behaviour}
                    />
                    {expandedSections.behaviour && (
                        <div className="p-6 space-y-6 border-t">
                            {/* A. Bite Sport Training */}
                            <div className="space-y-4 p-4 bg-secondary/20 rounded-lg">
                                <h4 className="font-semibold text-foreground">A. Bite Sport Training</h4>
                                <RadioGroup 
                                    label="Has your dog ever been trained in a bite sport?"
                                    name="biteSportTraining"
                                    value={formData.biteSportTraining}
                                    onChange={handleChange}
                                    options={yesNoOptions}
                                    error={errors.biteSportTraining}
                                />
                                {formData.biteSportTraining === 'yes' && (
                                    <div className="mt-4 pl-4 border-l-2 border-primary/30">
                                        <TextAreaField 
                                            label="Please describe the training"
                                            name="biteSportDetails"
                                            value={formData.biteSportDetails}
                                            onChange={handleChange}
                                            placeholder="Describe the bite sport training..."
                                            error={errors.biteSportDetails}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* B. Aggression History */}
                            <div className="space-y-4 p-4 bg-secondary/20 rounded-lg">
                                <h4 className="font-semibold text-foreground">B. Aggression History</h4>
                                <RadioGroup 
                                    label="Does your dog have a history of aggression or biting?"
                                    name="aggressionHistory"
                                    value={formData.aggressionHistory}
                                    onChange={handleChange}
                                    options={aggressionOptions}
                                    error={errors.aggressionHistory}
                                />
                                {formData.aggressionHistory === 'yes' && (
                                    <div className="mt-4 pl-4 border-l-2 border-primary/30">
                                        <TextAreaField 
                                            label="Please describe the circumstances"
                                            name="aggressionDetails"
                                            value={formData.aggressionDetails}
                                            onChange={handleChange}
                                            placeholder="Describe the aggression history..."
                                            error={errors.aggressionDetails}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* C. General Behaviour */}
                            <div className="space-y-4 p-4 bg-secondary/20 rounded-lg">
                                <h4 className="font-semibold text-foreground">C. General Behaviour</h4>
                                <TextAreaField 
                                    label="Please describe your dog's behaviour around people and other dogs"
                                    name="generalBehaviour"
                                    value={formData.generalBehaviour}
                                    onChange={handleChange}
                                    placeholder="Describe how your dog typically behaves..."
                                    rows={4}
                                    error={errors.generalBehaviour}
                                />
                            </div>

                            {/* D. Known Triggers */}
                            <div className="space-y-4 p-4 bg-secondary/20 rounded-lg">
                                <h4 className="font-semibold text-foreground">D. Known Triggers</h4>
                                <TextAreaField 
                                    label="Are there any known triggers?"
                                    name="knownTriggers"
                                    value={formData.knownTriggers}
                                    onChange={handleChange}
                                    placeholder="List any triggers or write 'none'..."
                                    error={errors.knownTriggers}
                                />
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Section 4: Environmental Tolerance */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={MapPin} 
                        title="4. Environmental Tolerance" 
                        section="environment"
                        expanded={expandedSections.environment}
                        onToggle={toggleSection}
                        isComplete={sectionCompletion.sections.environment}
                    />
                    {expandedSections.environment && (
                        <div className="p-6 border-t">
                            <TextAreaField 
                                label="How does your dog cope in public or unfamiliar environments?"
                                name="environmentalTolerance"
                                value={formData.environmentalTolerance}
                                onChange={handleChange}
                                placeholder="Describe how your dog handles different environments..."
                                rows={4}
                                error={errors.environmentalTolerance}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Section 5: Handler Safety Information */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={Phone} 
                        title="5. Handler Safety Information" 
                        section="handlerSafety"
                        expanded={expandedSections.handlerSafety}
                        onToggle={toggleSection}
                        isComplete={sectionCompletion.sections.handlerSafety}
                    />
                    {expandedSections.handlerSafety && (
                        <div className="p-6 space-y-4 border-t">
                            <TextAreaField 
                                label="Is there anything regarding your health or physical ability that we should know to make sure you're fully supported?"
                                name="physicalLimitations"
                                value={formData.physicalLimitations}
                                onChange={handleChange}
                                placeholder="Describe any limitations or write 'none'..."
                                error={errors.physicalLimitations}
                            />
                            <InputField 
                                label="Emergency Contact Number"
                                name="emergencyContact"
                                value={formData.emergencyContact}
                                onChange={handleChange}
                                type="tel"
                                placeholder="e.g., 07700 000000"
                                error={errors.emergencyContact}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Section 6: Safety Declarations */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={Shield} 
                        title="6. Safety Declarations" 
                        section="declarations"
                        expanded={expandedSections.declarations}
                        onToggle={toggleSection}
                        isComplete={sectionCompletion.sections.declarations}
                    />
                    {expandedSections.declarations && (
                        <div className="p-6 space-y-4 border-t">
                            <p className="text-sm text-muted-foreground mb-4">
                                All declarations must be checked:
                            </p>
                            <div className="space-y-4">
                                <CheckboxField 
                                    label="My dog is fit to train and insured"
                                    name="fitToTrain"
                                    checked={formData.fitToTrain}
                                    onChange={handleChange}
                                    error={errors.fitToTrain}
                                />
                                <CheckboxField 
                                    label="My dog will remain on lead at all times during training"
                                    name="onLeadAtAllTimes"
                                    checked={formData.onLeadAtAllTimes}
                                    onChange={handleChange}
                                    error={errors.onLeadAtAllTimes}
                                />
                                <CheckboxField 
                                    label="My dog will remain in my vehicle unless asked by the instructor"
                                    name="remainInVehicle"
                                    checked={formData.remainInVehicle}
                                    onChange={handleChange}
                                    error={errors.remainInVehicle}
                                />
                                <CheckboxField 
                                    label="I understand training may take place in public environments"
                                    name="publicEnvironments"
                                    checked={formData.publicEnvironments}
                                    onChange={handleChange}
                                    error={errors.publicEnvironments}
                                />
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Section 7: Legal & Risk Acknowledgement */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={FileText} 
                        title="7. Legal & Risk Acknowledgement" 
                        section="legal"
                        expanded={expandedSections.legal}
                        onToggle={toggleSection}
                        isComplete={sectionCompletion.sections.legal}
                    />
                    {expandedSections.legal && (
                        <div className="p-6 space-y-4 border-t">
                            <CheckboxField 
                                label="I have read and agree to the"
                                name="termsAccepted"
                                checked={formData.termsAccepted}
                                onChange={handleChange}
                                linkText="Terms and Conditions"
                                linkUrl="/terms"
                                error={errors.termsAccepted}
                            />
                            <CheckboxField 
                                label="I have read the"
                                name="riskAssessmentRead"
                                checked={formData.riskAssessmentRead}
                                onChange={handleChange}
                                linkText="risk assessment"
                                linkUrl="https://customer-assets.emergentagent.com/job_eaf279a0-83eb-443a-a424-79ded8f717a5/artifacts/hne32jxn_Craven%20Canines%20Risk%20Assessment.pdf"
                                error={errors.riskAssessmentRead}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Section 8: Additional Safety Information */}
            <Card>
                <CardContent className="p-0">
                    <SectionHeader 
                        icon={CheckCircle2} 
                        title="8. Additional Information" 
                        section="additional" 
                        required={false}
                        expanded={expandedSections.additional}
                        onToggle={toggleSection}
                    />
                    {expandedSections.additional && (
                        <div className="p-6 border-t">
                            <TextAreaField 
                                label="Is there anything else we should know?"
                                name="additionalInfo"
                                value={formData.additionalInfo}
                                onChange={handleChange}
                                placeholder="Any additional information (optional)..."
                                required={false}
                                error={errors.additionalInfo}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Footer Disclaimer */}
            <div className="bg-secondary/30 rounded-lg p-4 text-sm text-muted-foreground">
                <strong>Disclaimer:</strong> By booking a space on our session you agree to our Terms and Conditions:{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">
                    Terms and Conditions
                </Link>
            </div>

            {/* Auto-Save Indicator */}
            <AutoSaveIndicator isSaving={isSaving} lastSaved={lastSaved} />
        </div>
    );
};

export default SafetyForm;
